"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { FaPhone, FaPhoneSlash } from "react-icons/fa";
import { HiCalendar, HiCreditCard } from "react-icons/hi2";
import { RiLoader2Fill } from "react-icons/ri";
import PatientDisplay from "@/components/patient/patient-display";
import { Button } from "@/components/ui";
import Avatar from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { useLocations } from "@/hooks/use-locations";
import { useRingtone } from "@/hooks/use-ringtone";
import { formatCurrency, formatDate, formatPhoneNumber } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";
import { useInboundCall } from "./inbound-call-provider";
import { useCallerEnrichment } from "./use-caller-enrichment";
const InboundCallBanner: React.FC = () => {
  const $ = _c(51);
  const inbound = useInboundCall();
  const {
    locations
  } = useLocations();
  const matchedPatient = inbound?.matchedPatient ?? null;
  const {
    nextAppointment,
    isAppointmentsLoading,
    patientDetail
  } = useCallerEnrichment(matchedPatient?.id ?? null);
  useRingtone(inbound?.callState === "ringing");
  const isCancelled = inbound?.callState === "cancelled";
  const isRinging = inbound?.callState === "ringing";
  const isConnecting = inbound?.callState === "connecting";
  if (!inbound || !isRinging && !isConnecting && !isCancelled || !inbound.callerInfo) {
    return null;
  }
  const {
    callerName,
    callerNumber
  } = inbound.callerInfo;
  const isLoading = inbound.isPatientLookupLoading;
  const fallbackName = callerName && callerName !== "Unknown" ? callerName : null;
  let t0;
  if ($[0] !== callerNumber) {
    t0 = callerNumber ? formatPhoneNumber(callerNumber) || "Unknown Number" : "Unknown Number";
    $[0] = callerNumber;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const displayNumber = t0;
  const hasEnrichment = !!matchedPatient;
  const dialedLocationName = locations.length > 1 ? inbound.dialedLocationName : null;
  const t1 = isCancelled ? "border-border/40 bg-background/90 opacity-80" : "border-border bg-background/95 motion-preset-slide-down-md";
  const t2 = hasEnrichment ? "w-[540px]" : "w-auto min-w-[340px]";
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = cx("fixed left-1/2 top-4 z-[60] -translate-x-1/2", "max-w-[90vw] overflow-hidden rounded-2xl border", "shadow-[0_8px_30px_rgba(0,0,0,0.12)]", "backdrop-blur-xl transition-all duration-300 ease-out", t1, t2);
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const t4 = isCancelled ? "text-foreground-tertiary" : "text-foreground-secondary";
  let t5;
  if ($[5] !== t4) {
    t5 = cx("flex items-center gap-2 px-5 pb-0 pt-3", t4);
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== isCancelled) {
    t6 = isCancelled ? <FaPhoneSlash className="h-3 w-3" /> : <span className="relative flex h-2.5 w-2.5"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-status-success-text opacity-75" /><span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-status-success-text" /></span>;
    $[7] = isCancelled;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = isCancelled ? "Call Ended" : isConnecting ? "Connecting" : "Incoming Call";
  let t8;
  if ($[9] !== t7) {
    t8 = <span className="text-caption-strong uppercase tracking-wider">{t7}</span>;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== dialedLocationName) {
    t9 = dialedLocationName && <span className="truncate text-caption-strong uppercase tracking-wider">· {dialedLocationName}</span>;
    $[11] = dialedLocationName;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== t5 || $[14] !== t6 || $[15] !== t8 || $[16] !== t9) {
    t10 = <div className={t5}>{t6}{t8}{t9}</div>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t8;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== fallbackName || $[19] !== isCancelled || $[20] !== isLoading || $[21] !== matchedPatient) {
    t11 = <div className="flex-shrink-0">{isLoading ? <Skeleton className="h-14 w-14 rounded-full" /> : <div className={cx("rounded-full p-0.5", !isCancelled && matchedPatient && "ring-2 ring-status-success-text/30")}><Avatar src={matchedPatient?.profileImageUrl} fallback={matchedPatient ? `${matchedPatient.firstName} ${matchedPatient.lastName}` : fallbackName ?? undefined} size="xl" /></div>}</div>;
    $[18] = fallbackName;
    $[19] = isCancelled;
    $[20] = isLoading;
    $[21] = matchedPatient;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== displayNumber || $[24] !== fallbackName || $[25] !== isCancelled || $[26] !== isLoading || $[27] !== matchedPatient) {
    t12 = <div className="flex min-w-0 flex-1 flex-col">{isLoading ? <><Skeleton className="mb-1 h-6 w-36" /><Skeleton className="h-4 w-24" /></> : matchedPatient ? <PatientDisplay patient={matchedPatient} showPhoneNumber={true} showDateOfBirth={true} showMemberships={true} layout="vertical" useLinks={false} nameClassName={cx("text-h2 leading-tight", isCancelled && "text-foreground-secondary")} /> : <>{fallbackName && <h3 className={cx("truncate text-h2 leading-tight", isCancelled ? "text-foreground-secondary" : "text-foreground")}>{fallbackName}</h3>}<span className={cx("text-body tabular-nums", isCancelled ? "text-foreground-tertiary" : "text-foreground-secondary")}>{displayNumber}</span></>}</div>;
    $[23] = displayNumber;
    $[24] = fallbackName;
    $[25] = isCancelled;
    $[26] = isLoading;
    $[27] = matchedPatient;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== inbound.answer || $[30] !== inbound.cancelReason || $[31] !== inbound.decline || $[32] !== isCancelled || $[33] !== isConnecting) {
    t13 = isCancelled ? <span className="flex-shrink-0 text-body-strong text-foreground-tertiary">{inbound.cancelReason === "caller_hangup" ? "Caller hung up" : "No answer"}</span> : isConnecting ? <span className="flex flex-shrink-0 items-center gap-1.5 text-body-strong text-foreground-secondary"><RiLoader2Fill className="h-4 w-4 animate-spin" aria-hidden={true} />Connecting…</span> : <div className="flex flex-shrink-0 items-center gap-2"><Button onClick={inbound.decline} variant="destructive" className="h-10 w-10 cursor-pointer rounded-full p-0" aria-label="Decline call"><FaPhoneSlash className="h-4 w-4" /></Button><Button variant="secondary" onClick={inbound.answer} className="h-10 w-10 cursor-pointer rounded-full bg-status-success-bg p-0 text-status-success-text ring-1 ring-inset ring-status-success-text/20 hover:bg-status-success-bg/80" aria-label="Answer call"><FaPhone className="h-4 w-4" /></Button></div>;
    $[29] = inbound.answer;
    $[30] = inbound.cancelReason;
    $[31] = inbound.decline;
    $[32] = isCancelled;
    $[33] = isConnecting;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t11 || $[36] !== t12 || $[37] !== t13) {
    t14 = <div className="flex items-center gap-4 px-5 py-4">{t11}{t12}{t13}</div>;
    $[35] = t11;
    $[36] = t12;
    $[37] = t13;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== hasEnrichment || $[40] !== isAppointmentsLoading || $[41] !== isCancelled || $[42] !== matchedPatient || $[43] !== nextAppointment || $[44] !== patientDetail) {
    t15 = hasEnrichment && !isCancelled && <div className="border-t border-border/60 bg-background-secondary/50 px-5 py-2.5"><div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">{matchedPatient.creditBalance != null && matchedPatient.creditBalance > 0 && <span className="inline-flex items-center gap-1.5 rounded-md bg-background px-2 py-0.5 text-caption-strong text-foreground-secondary ring-1 ring-inset ring-border/60"><HiCreditCard className="h-3.5 w-3.5 text-foreground-muted" />{formatCurrency(matchedPatient.creditBalance)} credit</span>}{patientDetail?.totalSpent != null && patientDetail.totalSpent > 0 && <span className="inline-flex items-center gap-1.5 rounded-md bg-background px-2 py-0.5 text-caption-strong text-foreground-secondary ring-1 ring-inset ring-border/60"><HiCreditCard className="h-3.5 w-3.5 text-foreground-muted" />{formatCurrency(patientDetail.totalSpent)} spent</span>}{isAppointmentsLoading ? <Skeleton className="h-5 w-28 rounded-md" /> : nextAppointment ? <span className="inline-flex items-center gap-1.5 rounded-md bg-background px-2 py-0.5 text-caption-strong text-foreground-secondary ring-1 ring-inset ring-border/60"><HiCalendar className="h-3.5 w-3.5 text-accent-foreground" /><span>{formatDate(nextAppointment.start, true, false)}<span>{nextAppointment.serviceNames.length > 0 ? ` \u00b7 ${nextAppointment.serviceNames[0]}` : ""}</span></span></span> : null}</div></div>;
    $[39] = hasEnrichment;
    $[40] = isAppointmentsLoading;
    $[41] = isCancelled;
    $[42] = matchedPatient;
    $[43] = nextAppointment;
    $[44] = patientDetail;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== t10 || $[47] !== t14 || $[48] !== t15 || $[49] !== t3) {
    t16 = <div className={t3}>{t10}{t14}{t15}</div>;
    $[46] = t10;
    $[47] = t14;
    $[48] = t15;
    $[49] = t3;
    $[50] = t16;
  } else {
    t16 = $[50];
  }
  return t16;
};
export default InboundCallBanner;